<template>
  <div data-testid="filestack-container" @click.prevent="onClickPick">
    <slot></slot>
  </div>
</template>

<script>
import * as filestack from 'filestack-js';

export default {
  name: 'Filestack',
  props: {
    apikey: {
      type: String,
      default: ''
    },
    file: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'pick'
    },
    options: {
      type: Object,
      default: function () {
        return {};
      }
    },
    security: {
      type: Object,
      default: null
    },
    cname: {
      type: String,
      default: null
    },
    refreshSecurity: {
      type: Function,
      default: null
    },
    refreshSecurityForFile: {
      type: Function,
      default: null
    },
    invalidateSecurity: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      client: null,
      picker: null,
      isPickerSessionActive: false
    };
  },
  async created() {
    if (this.options.displayMode === 'inline') {
      await this.openFilestack();
    }
  },
  methods: {
    onSuccess(result) {
      this.$emit('success', result);
    },
    onError(err) {
      this.$emit('error', err);
    },
    isPickerMode() {
      return !['transform', 'retrieve', 'metadata', 'storeUrl', 'upload', 'remove'].includes(this.mode);
    },
    async resolveSecurityForOpen() {
      // Picker uploads sign in onFileSelected instead, once the file (and its size) is known.
      if (this.isPickerMode() && this.refreshSecurityForFile) {
        return this.security;
      }
      let security = this.security;
      if (this.refreshSecurity) {
        try {
          security = (await this.refreshSecurity()) || security;
        } catch (e) {
          // fall back to whatever security prop was already bound; initClient will
          // surface an auth failure to the caller if it's expired
        }
      }
      return security;
    },
    async onClickPick() {
      // Filestack mounts one picker root per page, so opening a second picker while one is live is
      // a no-op on their side. Re-initialising anyway would leave the live picker uploading with the
      // previous client while onFileSelected signs the newest one, and the upload goes out unsigned.
      if (this.isPickerMode() && this.isPickerSessionActive) {
        return;
      }
      await this.openFilestack();
    },
    async openFilestack() {
      try {
        const security = await this.resolveSecurityForOpen();
        const result = await this.initClient(this.mode, this.apikey, this.options, this.file, security, this.cname);
        this.onSuccess(result);
      } catch (error) {
        this.isPickerSessionActive = false;
        this.onError(error);
      }
    },
    initClient(mode, apikey, options, file, security, cname) {
      this.$emit('init');
      const url = options.url;
      const handle = options.handle;
      const clientOptions = {};
      if (security) {
        clientOptions.security = security;
      }
      if (cname) {
        clientOptions.cname = cname;
      }
      // Everything below binds to this local client instead of this.client: a picker keeps the client
      // it was built with, so signing must target that client even if a newer one has been created.
      const client = filestack.init(apikey, clientOptions);
      this.client = client;
      const ops = Object.assign({}, options);
      delete ops.handle;
      delete ops.url;
      let picker = null;
      client.on('upload.error', filestackError => {
        if (filestackError?.type == 'aborted') {
          picker?.close();
          return;
        }
        this.invalidateSecurity?.(filestackError);
        this.$emit('error', filestackError);
        picker?.close();
      });
      if (mode === 'transform') {
        return new Promise((resolve, reject) => {
          try {
            resolve(client.transform(url, ops));
          } catch (err) {
            reject(err);
          }
        });
      }
      if (mode === 'retrieve') {
        return client.retrieve(handle, ops);
      }
      if (mode === 'metadata') {
        return client.metadata(handle, ops);
      }
      if (mode === 'storeUrl') {
        return client.storeURL(url, ops);
      }
      if (mode === 'upload') {
        return client.upload(file, ops, undefined, undefined, security);
      }
      if (mode === 'remove') {
        return client.remove(handle, security);
      }
      if (this.refreshSecurityForFile) {
        const onFileSelected = ops.onFileSelected;
        ops.onFileSelected = async file => {
          let result = file;
          if (onFileSelected) {
            result = (await onFileSelected(file)) ?? file;
          }
          const refreshed = await this.refreshSecurityForFile(result);
          if (refreshed) {
            client.setSecurity(refreshed);
          }
          return result;
        };
      }
      const onClose = ops.onClose;
      ops.onClose = (...args) => {
        this.isPickerSessionActive = false;
        return onClose?.(...args);
      };
      this.isPickerSessionActive = true;
      picker = client.picker(ops);
      this.picker = picker;
      return picker.open();
    }
  }
};
</script>
